import React, { useState } from 'react';
import { Modal, Input, Button } from '@/components/shared/ui';
import { X } from 'lucide-react';
import AlertDialog from '@/components/shared/ui/AlertDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userServiceApi } from '@/services/userServiceApi';
import type { SearchRegularMemberForMasterResponseDto } from '@/types/api';

interface AddMasterMemberModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    organizationId?: number;
}

export default function AddMasterMemberModal({
    isOpen,
    onClose,
    onSuccess,
    organizationId,
}: AddMasterMemberModalProps): React.ReactElement {
    const [loginId, setLoginId] = useState<string>('');
    const [realName, setRealName] = useState<string>('');
    const [selectedUsers, setSelectedUsers] = useState<SearchRegularMemberForMasterResponseDto[]>([]);
    const [alertState, setAlertState] = useState({
        isOpen: false,
        type: 'success' as 'success' | 'error',
        title: '',
        message: '',
    });
    const queryClient = useQueryClient();

    // 정회원 검색 API (마스터용)
    const searchMutation = useMutation({
        mutationFn: (params: { realName: string; loginId: string }) =>
            userServiceApi.searchRegularMemberForMaster(params),
    });

    // 기관 정회원 등록 API
    const registerMutation = useMutation({
        mutationFn: (userIdList: number[]) =>
            userServiceApi.registerOrganizationMembers({ organizationId: organizationId!, userIdList }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userManagement'] });
        },
    });

    const handleSearch = async () => {
        const trimmedLoginId = loginId.trim();
        const trimmedRealName = realName.trim();

        if (!trimmedLoginId || !trimmedRealName) {
            setAlertState({
                isOpen: true,
                type: 'error',
                title: '입력 오류',
                message: 'ID와 이름을 모두 입력해주세요.',
            });
            return;
        }

        try {
            const result = await searchMutation.mutateAsync({
                loginId: trimmedLoginId,
                realName: trimmedRealName,
            });

            // 이미 추가된 사용자인지 확인
            if (selectedUsers.some((u) => u.userId === result.userId)) {
                setAlertState({
                    isOpen: true,
                    type: 'error',
                    title: '중복',
                    message: '이미 목록에 추가된 사용자입니다.',
                });
                return;
            }

            // 목록에 추가
            setSelectedUsers((prev) => [...prev, result]);
            // 입력 필드 초기화
            setLoginId('');
            setRealName('');
        } catch (err) {
            // 404 에러 처리
            if (err && typeof err === 'object' && 'status' in err && err.status === 404) {
                setAlertState({
                    isOpen: true,
                    type: 'error',
                    title: '검색 실패',
                    message: '일치하는 회원을 찾을 수 없습니다.',
                });
                return;
            }
            
            const msg = err instanceof Error ? err.message : '검색에 실패했습니다.';
            setAlertState({
                isOpen: true,
                type: 'error',
                title: '검색 실패',
                message: msg,
            });
        }
    };

    const handleRemoveUser = (userId: number) => {
        setSelectedUsers((prev) => prev.filter((u) => u.userId !== userId));
    };

    const handleSubmit = async () => {
        if (selectedUsers.length === 0) {
            setAlertState({
                isOpen: true,
                type: 'error',
                title: '회원 선택 필요',
                message: '추가할 회원을 검색해주세요.',
            });
            return;
        }

        try {
            if (organizationId) {
                await registerMutation.mutateAsync(selectedUsers.map((u) => u.userId));
            }
            setAlertState({
                isOpen: true,
                type: 'success',
                title: '정회원 추가 완료',
                message: `${selectedUsers.length}명의 회원이 추가되었습니다.`,
            });
            onSuccess?.();
            setTimeout(handleClose, 1200);
        } catch (err) {
            const msg = err instanceof Error ? err.message : '회원 추가에 실패했습니다.';
            setAlertState({
                isOpen: true,
                type: 'error',
                title: '추가 실패',
                message: msg,
            });
        }
    };

    const handleClose = () => {
        setLoginId('');
        setRealName('');
        setSelectedUsers([]);
        onClose();
    };

    const footer = (
        <>
            <Button variant="secondary" size="md" onClick={handleClose}>
                취소
            </Button>
            <Button
                variant="primary"
                size="md"
                onClick={handleSubmit}
                disabled={selectedUsers.length === 0 || registerMutation.isPending}
            >
                {selectedUsers.length > 0 ? `${selectedUsers.length}명 추가` : '추가'}
            </Button>
        </>
    );

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="정회원 추가" footer={footer} size="medium">
            <div className="space-y-4 min-h-[400px]">
                <div className="text-center bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-700 space-y-1">
                    <p>정회원 계정은 유효기간 제한없이 사용 가능합니다.</p>
                    <p>마스터 권한은 정회원만 가능합니다.</p>
                </div>

                {/* 검색 입력 */}
                <div className="flex gap-3 items-end">
                    <Input
                        type="text"
                        id="login-id-input"
                        label="ID"
                        value={loginId}
                        onChange={(e) => setLoginId(e.target.value)}
                        placeholder="로그인 ID 입력"
                        aria-label="로그인 ID"
                        className="flex-1"
                    />
                    <Input
                        type="text"
                        id="real-name-input"
                        label="이름"
                        value={realName}
                        onChange={(e) => setRealName(e.target.value)}
                        placeholder="이름 입력"
                        aria-label="이름"
                        className="flex-1"
                    />
                    <Button
                        variant="outline"
                        size="md"
                        onClick={handleSearch}
                        disabled={searchMutation.isPending}
                        className="shrink-0"
                    >
                        검색
                    </Button>
                </div>

                {/* 검색 결과 테이블 */}
                {selectedUsers.length > 0 && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            추가할 목록 ({selectedUsers.length}명)
                        </label>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            이름
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            아이디
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            이메일
                                        </th>
                                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-16">
                                            삭제
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {selectedUsers.map((user) => (
                                        <tr key={user.userId} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {user.realName}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {user.loginId}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {user.email || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveUser(user.userId)}
                                                    className="inline-flex items-center justify-center w-6 h-6 text-gray-400 hover:text-red-600 transition-colors"
                                                    aria-label="삭제"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {selectedUsers.length === 0 && (
                    <div className="text-center py-8 text-gray-500 text-sm">
                        ID와 이름을 입력하고 검색 버튼을 눌러주세요.
                    </div>
                )}
            </div>

            <AlertDialog
                isOpen={alertState.isOpen}
                onClose={() => setAlertState((prev) => ({ ...prev, isOpen: false }))}
                title={alertState.title}
                message={alertState.message}
            />
        </Modal>
    );
}
